import {
  LoaderFunction,
  LoaderFunctionArgs,
  RouteObject,
  redirect,
} from "react-router";
import { AgentFeature, isAgentFeatureAvailable } from "@/configs/agentFeatures";
import { URLs } from "@/configs/menu";
import { UserContext } from "@/context/UserContext";
import { ConfigParameter } from "@/types/api";

/**
 * Run an existing route loader, then redirect to the root unless the tenant
 * has `flag`. Shared by `featureFlaggedRoutes` and `mockScopedRoutes`.
 */
export const composeFeatureFlagLoader = (
  flag: ConfigParameter,
  existing?: LoaderFunction,
): LoaderFunction =>
  composeAvailabilityLoader(
    () => UserContext.hasConfigParameter(flag),
    existing,
  );

/** Run an existing route loader, then redirect to the root unless `isAvailable()`. */
const composeAvailabilityLoader = (
  isAvailable: () => boolean,
  existing?: LoaderFunction,
): LoaderFunction => {
  return async (args: LoaderFunctionArgs) => {
    if (existing) {
      const result = await existing(args);
      if (result) return result;
    }
    if (!isAvailable()) {
      return redirect(URLs.root);
    }
    return null;
  };
};

/**
 * Gate routes on a part of the new agent experience having a backend: always
 * reachable in demo mode, and in live mode only once the feature is `live` in
 * `@/configs/agentFeatures`. Nest inside `mockScopedRoutes(AGENT_ONBOARDING,
 * ...)`, which adds the `isNewAgentOnboarding` gate on top.
 */
export const agentFeatureRoutes = <R extends { loader?: unknown }>(
  feature: AgentFeature,
  ...routes: R[]
): R[] =>
  routes.map(route => ({
    ...route,
    loader: composeAvailabilityLoader(
      () => isAgentFeatureAvailable(feature),
      route.loader as LoaderFunction | undefined,
    ),
  }));

/**
 * Declare one or more routes gated by a tenant feature flag.
 *
 *   ...featureFlaggedRoutes("isEnterprisePolicyEnabled",
 *     authRoute(URLs.enterprise_policies, () => import("@/pages/EnterprisePolicies")),
 *   )
 *
 * `authRoute`'s own loader already enforces the `featureConfig` of a *matching*
 * navigation entry, but `matchRoutePattern` compares whole pathnames — so a
 * detail route like `/app/enterprise_policies/:id` finds no entry and is waved
 * through. Wrap detail routes here so they inherit the section's flag.
 *
 * Pair with a navigation entry that has `featureConfig: flag` so the sidebar
 * hides the link too. Use `mockScopedRoutes` instead when the routes should
 * also render under a `<MockScope>`.
 */
export const featureFlaggedRoutes = (
  flag: ConfigParameter,
  ...routes: RouteObject[]
): RouteObject[] =>
  routes.map(route => ({
    ...route,
    loader: composeFeatureFlagLoader(
      flag,
      route.loader as LoaderFunction | undefined,
    ),
  }));
